import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Upload, Save, X, PenLine, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SignatureCanvas } from '@/components/crm/SignatureCanvas';
import { formatPhoneNumber } from '@/lib/phoneUtils';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function InformationsEntreprise() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPartenaire, setUploadingPartenaire] = useState(false);
  const [uploadingLogoEmail, setUploadingLogoEmail] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(null); // null, 0, or 1

  const [formData, setFormData] = useState({
    nom_entreprise: '',
    slogan: '',
    description: '',
    telephone: '',
    email: '',
    site_web: '',
    lien_avis_google: '',
    adresse: '',
    ville: '',
    province: 'Québec',
    code_postal: '',
    numero_neq: '',
    numero_rbq: '',
    numero_tps: '',
    numero_tvq: '',
    logo_principal: '',
    logo_email: '',
    logos_partenaires: [],
    signatures_actionnaires: [],
  });

  useEffect(() => {
    fetchInfos();
  }, []);

  const fetchInfos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/entreprise/infos`);
      if (response.status !== 200) throw new Error('Erreur');
      const data = response.data;
      // Migrate old single signature to array if needed
      if (!data.signatures_actionnaires && data.signature_commercant) {
        data.signatures_actionnaires = [{ nom: data.nom_signataire_commercant || '', signature: data.signature_commercant }];
      }
      if (!data.signatures_actionnaires) data.signatures_actionnaires = [];
      setFormData(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nom_entreprise || !formData.telephone || !formData.email) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/entreprise/infos`, formData);
      if (response.status !== 200) throw new Error('Erreur');
      toast.success('Informations enregistrées avec succès!');
      // Re-fetch pour confirmer la persistance
      await fetchInfos();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (e, type = 'principal') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === 'principal') setUploadingLogo(true);
    else setUploadingPartenaire(true);
    try {
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      const compressed = await compressImageIfNeeded(file);
      const fd = new FormData();
      fd.append('file', compressed);
      const response = await axios.post(`${API_URL}/api/entreprise/upload-logo?type_logo=${type}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.status !== 200) throw new Error('Erreur upload');
      const data = response.data;
      if (type === 'principal') {
        setFormData(prev => ({ ...prev, logo_principal: data.logo_url }));
        toast.success('Logo principal uploadé!');
      } else {
        setFormData(prev => ({ ...prev, logos_partenaires: [...prev.logos_partenaires, data.logo_url] }));
        toast.success('Logo partenaire ajouté!');
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      if (type === 'principal') setUploadingLogo(false);
      else setUploadingPartenaire(false);
    }
  };

  const supprimerLogoPartenaire = (index) => {
    setFormData(prev => ({ ...prev, logos_partenaires: prev.logos_partenaires.filter((_, i) => i !== index) }));
  };

  const handleUploadLogoEmail = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogoEmail(true);
    try {
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      const compressed = await compressImageIfNeeded(file);
      const fd = new FormData();
      fd.append('file', compressed);
      const response = await axios.post(`${API_URL}/api/entreprise/upload-logo?type_logo=email`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.status !== 200) throw new Error('Erreur upload');
      const data = response.data;
      setFormData(prev => ({ ...prev, logo_email: data.logo_url }));
      toast.success('Logo email uploadé!');
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingLogoEmail(false);
    }
  };

  const handleSignatureSave = (signatureData, nomSignataire) => {
    const idx = showSignaturePad;
    setFormData(prev => {
      const sigs = [...(prev.signatures_actionnaires || [])];
      sigs[idx] = { nom: nomSignataire, signature: signatureData };
      return { ...prev, signatures_actionnaires: sigs };
    });
    setShowSignaturePad(null);
    toast.success('Signature enregistrée! N\'oubliez pas de sauvegarder.');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Informations de l'Entreprise</h1>
        <p className="text-gray-600 mt-1 text-sm">Ces informations seront utilisées pour les soumissions, factures et emails</p>
      </div>

      <div className="space-y-6">
        {/* Identité */}
        <Card>
          <CardHeader><CardTitle>Identité de l'Entreprise</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nom de l'Entreprise <span className="text-red-500">*</span></Label>
              <Input value={formData.nom_entreprise} onChange={(e) => setFormData({ ...formData, nom_entreprise: e.target.value })} placeholder="Peinture Résidentielle QC" />
            </div>
            <div>
              <Label>Slogan</Label>
              <Input value={formData.slogan} onChange={(e) => setFormData({ ...formData, slogan: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader><CardTitle>Coordonnées</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Téléphone <span className="text-red-500">*</span></Label>
                <Input value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: formatPhoneNumber(e.target.value) })} placeholder="(514) 555-1234" />
              </div>
              <div>
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="info@entreprise.com" />
              </div>
            </div>
            <div>
              <Label>Site Web</Label>
              <Input value={formData.site_web} onChange={(e) => setFormData({ ...formData, site_web: e.target.value })} placeholder="https://www.entreprise.com" />
            </div>
            <div>
              <Label>Lien Avis Google</Label>
              <Input value={formData.lien_avis_google} onChange={(e) => setFormData({ ...formData, lien_avis_google: e.target.value })} placeholder="https://g.page/r/votre-entreprise/review" />
              <p className="text-xs text-gray-500 mt-1">Ce lien sera utilisé dans les emails pour "Avis Google"</p>
            </div>
          </CardContent>
        </Card>

        {/* Adresse */}
        <Card>
          <CardHeader><CardTitle>Adresse</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Adresse</Label>
              <Input value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} placeholder="123 Rue Principale" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Ville</Label>
                <Input value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} placeholder="Montréal" />
              </div>
              <div>
                <Label>Province</Label>
                <Input value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} />
              </div>
              <div>
                <Label>Code Postal</Label>
                <Input value={formData.code_postal} onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })} placeholder="H1A 1A1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informations Légales */}
        <Card>
          <CardHeader><CardTitle>Informations Légales</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Numéro NEQ</Label>
                <Input value={formData.numero_neq} onChange={(e) => setFormData({ ...formData, numero_neq: e.target.value })} placeholder="1234567890" />
              </div>
              <div>
                <Label>Numéro RBQ</Label>
                <Input value={formData.numero_rbq} onChange={(e) => setFormData({ ...formData, numero_rbq: e.target.value })} placeholder="1234-5678-90" />
              </div>
              <div>
                <Label>Numéro TPS</Label>
                <Input value={formData.numero_tps} onChange={(e) => setFormData({ ...formData, numero_tps: e.target.value })} placeholder="123456789 RT0001" />
              </div>
            </div>
            <div>
              <Label>Numéro TVQ</Label>
              <Input value={formData.numero_tvq} onChange={(e) => setFormData({ ...formData, numero_tvq: e.target.value })} placeholder="1234567890 TQ0001" />
            </div>
          </CardContent>
        </Card>

        {/* Logos */}
        <Card>
          <CardHeader><CardTitle>Logos</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Logo Principal</Label>
              <div className="mt-2 flex items-center gap-4">
                {formData.logo_principal && (
                  <img src={`${API_URL}${formData.logo_principal}`} alt="Logo" className="h-24 w-auto border rounded" />
                )}
                <div>
                  <input type="file" id="logo-principal" accept="image/*" onChange={(e) => handleUploadLogo(e, 'principal')} className="hidden" />
                  <Button type="button" variant="outline" disabled={uploadingLogo} onClick={() => document.getElementById('logo-principal').click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingLogo ? 'Upload...' : formData.logo_principal ? 'Changer' : 'Upload Logo'}
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <Label>Logos Partenaires</Label>
              <div className="mt-2 space-y-4">
                {formData.logos_partenaires.length > 0 && (
                  <div className="grid grid-cols-4 gap-4">
                    {formData.logos_partenaires.map((logoUrl, index) => (
                      <div key={logoUrl.id || logoUrl._id || index} className="relative border rounded p-2">
                        <img src={`${API_URL}${logoUrl}`} alt={`Partenaire ${index + 1}`} className="h-16 w-auto mx-auto" />
                        <Button size="sm" variant="ghost" className="absolute top-0 right-0" onClick={() => supprimerLogoPartenaire(index)}>
                          <X className="w-3 h-3 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <input type="file" id="logo-partenaire" accept="image/*" onChange={(e) => handleUploadLogo(e, 'partenaire')} className="hidden" />
                  <Button type="button" variant="outline" disabled={uploadingPartenaire} onClick={() => document.getElementById('logo-partenaire').click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingPartenaire ? 'Upload...' : 'Ajouter Logo Partenaire'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logo pour signatures email */}
        <Card>
          <CardHeader><CardTitle>Logo pour signatures email</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Ce logo apparaîtra dans le pied de page de vos courriels automatiques. Utilisez la variable <code className="bg-gray-100 px-1 rounded text-xs font-mono">{'{logo_email}'}</code> dans vos templates.
            </p>
            <div className="flex items-center gap-4">
              {formData.logo_email && (
                <div className="relative border rounded p-2">
                  <img src={`${API_URL}${formData.logo_email}`} alt="Logo email" className="h-20 w-auto" data-testid="logo-email-preview" />
                  <Button size="sm" variant="ghost" className="absolute -top-2 -right-2" onClick={() => setFormData(prev => ({ ...prev, logo_email: '' }))}>
                    <X className="w-3 h-3 text-red-600" />
                  </Button>
                </div>
              )}
              <div>
                <input type="file" id="logo-email" accept="image/png,image/jpeg,image/webp" onChange={handleUploadLogoEmail} className="hidden" />
                <Button type="button" variant="outline" disabled={uploadingLogoEmail} onClick={() => document.getElementById('logo-email').click()} data-testid="upload-logo-email-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogoEmail ? 'Upload...' : formData.logo_email ? 'Changer le logo' : 'Upload Logo Email (PNG)'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><PenLine className="w-5 h-5" /> Signatures des Actionnaires</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">Ces signatures seront disponibles pour sélection dans vos soumissions.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[0, 1].map((idx) => {
                const sig = (formData.signatures_actionnaires || [])[idx];
                const hasValidSig = sig?.signature && sig.signature.startsWith('data:image/');
                return (
                  <div key={`actionnaire-${sig.nom || ''}-${idx}`} className="border rounded-lg p-4" data-testid={`actionnaire-${idx}`}>
                    <h4 className="font-semibold text-sm mb-3">Actionnaire {idx + 1}</h4>
                    {hasValidSig ? (
                      <div>
                        <img src={sig.signature} alt={`Signature ${sig.nom}`} className="border rounded h-24 bg-white p-1 w-full object-contain" />
                        <p className="text-sm mt-2 font-medium">{sig.nom}</p>
                        <div className="flex gap-2 mt-2">
                          <Button variant="outline" size="sm" onClick={() => setShowSignaturePad(idx)}>
                            <PenLine className="w-3 h-3 mr-1" /> Modifier
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            setFormData(prev => {
                              const sigs = [...(prev.signatures_actionnaires || [])];
                              sigs[idx] = null;
                              return { ...prev, signatures_actionnaires: sigs };
                            });
                          }}>
                            <Trash2 className="w-3 h-3 mr-1 text-red-600" /> Supprimer
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" onClick={() => setShowSignaturePad(idx)} className="w-full border-dashed h-24 flex flex-col gap-1" data-testid={`add-signature-${idx}`}>
                        <PenLine className="w-5 h-5" />
                        <span className="text-xs">Enregistrer la signature</span>
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Dialog Signature Actionnaire */}
        {showSignaturePad !== null && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4">
              <h3 className="text-lg font-bold mb-4">Signature — Actionnaire {showSignaturePad + 1}</h3>
              <SignatureCanvas
                onSave={handleSignatureSave}
                onCancel={() => setShowSignaturePad(null)}
              />
            </div>
          </div>
        )}

        {/* Bouton Sauvegarder */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-infos-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer les Informations'}
          </Button>
        </div>
      </div>
    </div>
  );
}
